/**
 * ProductComparisonPage
 * ----------------------
 * Side-by-side comparison of supplier products that have been flagged with
 * `compare=true` on the Products page. Products are grouped by industrial
 * product (and category), and each group lists every supplier offer sorted
 * by unit_price ASC so the cheapest is first.
 *
 * Printable on demand via window.print() — the layout uses Tailwind print:
 * utilities + a dedicated <style> block to hide non-essential UI (header,
 * back button, "Vider" action) and trim margins for an A4 portrait export.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, X, Loader2, BarChart3, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supplierProductsAPI } from '@/lib/api';

const ProductComparisonPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ groups: [], total_products: 0, total_industrial_products: 0 });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await supplierProductsAPI.getComparison();
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du chargement du comparatif');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleRemove = async (spId) => {
        try {
            await supplierProductsAPI.toggleCompare(spId, false);
            load();
        } catch (err) {
            toast.error('Erreur lors du retrait du produit');
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Retirer tous les produits du comparatif ?')) return;
        try {
            const { data } = await supplierProductsAPI.clearComparison();
            toast.success(`${data.cleared} produit${data.cleared > 1 ? 's' : ''} retiré${data.cleared > 1 ? 's' : ''}`);
            load();
        } catch (err) {
            toast.error('Erreur lors du nettoyage');
        }
    };

    // Group display: when multiple industrial products share the same category,
    // we render a category-level header so the print-out has clear sections.
    const groupedByCategory = useMemo(() => {
        const out = {};
        for (const g of data.groups) {
            const cat = g.category || 'Sans categorie';
            if (!out[cat]) out[cat] = [];
            out[cat].push(g);
        }
        return out;
    }, [data.groups]);

    return (
        <div className="min-h-screen bg-slate-50 print:bg-white">
            {/* Print-only style: hide chrome, trim margins. */}
            <style>{`
                @media print {
                    @page { size: A4 landscape; margin: 10mm; }
                    .no-print { display: none !important; }
                    .print-break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
                    /* Force backgrounds/colors to render on paper — without this
                       the emerald-50 best-price highlight and the indigo Badge
                       come out white in most browsers. */
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                }
            `}</style>

            {/* Header — hidden when printing */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
                <div className="max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/products')}
                            data-testid="comparison-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Retour
                        </Button>
                        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-indigo-500" />
                            Comparatif Produits
                        </h1>
                        <Badge variant="secondary" data-testid="comparison-count">
                            {data.total_products} produit{data.total_products > 1 ? 's' : ''} ·
                            {' '}{data.total_industrial_products} catégorie{data.total_industrial_products > 1 ? 's' : ''}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClear}
                            disabled={loading || data.total_products === 0}
                            data-testid="comparison-clear-btn"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Vider
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => window.print()}
                            disabled={loading || data.total_products === 0}
                            className="bg-indigo-500 hover:bg-indigo-600 text-white"
                            data-testid="comparison-print-btn"
                        >
                            <Printer className="w-4 h-4 mr-1" />
                            Imprimer
                        </Button>
                    </div>
                </div>
            </div>

            {/* Print-only title (only renders during print) */}
            <h1 className="hidden print:block text-2xl font-bold text-center mb-4 mt-2">
                Comparatif Produits — {new Date().toLocaleDateString('fr-FR')}
            </h1>

            <div className="max-w-screen-2xl mx-auto px-3 py-3 print:px-0 print:py-0">
                {loading && (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                )}

                {!loading && data.total_products === 0 && (
                    <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
                        <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-600 font-medium">Aucun produit à comparer</p>
                        <p className="text-sm text-slate-400 mt-1">
                            Cochez l&apos;icône <BarChart3 className="w-3 h-3 inline-block" /> à côté
                            d&apos;un produit fournisseur sur la page Produits pour l&apos;ajouter au comparatif.
                        </p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => navigate('/products')}
                        >
                            Aller à la page Produits
                        </Button>
                    </div>
                )}

                {/* Compact category sections — one section per category, then a
                    card per industrial product. Spacing tightened to maximise
                    information density on screen and on paper. */}
                {!loading && Object.entries(groupedByCategory).map(([category, groups]) => (
                    <div key={category} className="mb-2">
                        <h2 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1 print-break-inside-avoid px-1">
                            {category}
                        </h2>
                        <div className="space-y-1.5">
                            {groups.map(group => (
                                <ComparisonCard
                                    key={group.industrial_product_id}
                                    group={group}
                                    onRemove={handleRemove}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Single industrial product comparison card. Shows the product name + image,
 * then a sortable table with one row per supplier offer.
 */
const ComparisonCard = ({ group, onRemove }) => {
    return (
        <div
            className="bg-white border border-slate-200 rounded overflow-hidden shadow-sm print-break-inside-avoid"
            data-testid={`comparison-group-${group.industrial_product_id}`}
        >
            <div className="px-2 py-1 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    {group.image_url ? (
                        <img
                            src={group.image_url}
                            alt={group.industrial_product_name}
                            className="w-6 h-6 object-cover rounded border border-slate-200 flex-shrink-0"
                        />
                    ) : (
                        <div className="w-6 h-6 rounded border border-slate-200 bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Package className="w-3 h-3 text-slate-400" />
                        </div>
                    )}
                    <div className="min-w-0 leading-tight">
                        <span className="font-semibold text-xs text-slate-800 truncate">{group.industrial_product_name}</span>
                        <span className="ml-1.5 text-[10px] text-slate-400">
                            {group.unit}{group.famille ? ` · ${group.famille}` : ''}
                        </span>
                    </div>
                </div>
                <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {group.products.length} offre{group.products.length > 1 ? 's' : ''}
                </span>
            </div>
            <table className="w-full text-[11px]">
                <thead className="bg-white">
                    <tr className="text-slate-400 border-b border-slate-100 text-[10px] uppercase">
                        <th className="text-left px-2 py-0.5 font-medium">Fournisseur</th>
                        <th className="text-left px-2 py-0.5 font-medium">Désignation</th>
                        <th className="text-left px-2 py-0.5 font-medium">Marque</th>
                        <th className="text-left px-2 py-0.5 font-medium">Cond.</th>
                        <th className="text-right px-2 py-0.5 font-medium">Prix u.</th>
                        <th className="text-right px-2 py-0.5 font-medium">Prix/unité</th>
                        <th className="px-1 py-0.5 no-print w-6"></th>
                    </tr>
                </thead>
                <tbody>
                    {group.products.map((sp, i) => {
                        const cheapest = i === 0 && group.products.length > 1;
                        const pricePerUnit = sp.capacity_value
                            ? (sp.unit_price / sp.capacity_value).toFixed(3)
                            : null;
                        return (
                            <tr
                                key={sp.id}
                                className={`border-b border-slate-50 ${cheapest ? 'bg-emerald-50' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                                data-testid={`comparison-row-${sp.id}`}
                            >
                                <td className="px-2 py-0.5 font-medium text-slate-700 whitespace-nowrap">
                                    {sp.supplier_name}
                                    {cheapest && (
                                        <span className="ml-1 text-[9px] font-bold text-emerald-700">★</span>
                                    )}
                                </td>
                                <td className="px-2 py-0.5 text-slate-700">
                                    <span className="truncate">{sp.supplier_name_product}</span>
                                    {sp.supplier_ref && (
                                        <span className="ml-1 text-[9px] text-slate-400 font-mono">{sp.supplier_ref}</span>
                                    )}
                                </td>
                                <td className="px-2 py-0.5 text-slate-600">{sp.brand || '-'}</td>
                                <td className="px-2 py-0.5 text-slate-600 whitespace-nowrap">
                                    {sp.packaging || '-'}
                                    {sp.capacity_value && sp.capacity_unit && (
                                        <span className="ml-1 text-[9px] text-slate-400">
                                            ({sp.capacity_value}{sp.capacity_unit})
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 py-0.5 text-right font-mono font-semibold text-slate-800 whitespace-nowrap">
                                    {(sp.unit_price || 0).toFixed(2)}€
                                    <span className="ml-0.5 text-[9px] text-slate-400">/{sp.billing_unit || sp.order_unit}</span>
                                </td>
                                <td className="px-2 py-0.5 text-right font-mono text-slate-600 whitespace-nowrap">
                                    {pricePerUnit ? `${pricePerUnit}€/${sp.capacity_unit}` : '-'}
                                </td>
                                <td className="px-1 py-0.5 no-print">
                                    <button
                                        onClick={() => onRemove(sp.id)}
                                        className="text-slate-300 hover:text-red-500"
                                        title="Retirer"
                                        data-testid={`comparison-remove-${sp.id}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ProductComparisonPage;
